export const LIBRARY_KINDS = ['character-action', 'body', 'turnaround', 'emotion-row', 'drive', 'liftoff', 'flight', 'scene', 'social-clip'];

const KIND_LABELS = {
  'character-action': 'Activity',
  body: 'Full body',
  turnaround: 'Turnaround',
  'emotion-row': 'Emotion',
  drive: 'Drive',
  liftoff: 'Liftoff',
  flight: 'Flight',
  scene: 'Scene',
  'social-clip': 'Social clip',
};

export function isArchivedClip(generation) {
  return generation?.status === 'archived' || Boolean(generation?.archivedAt) || Boolean(generation?.archived);
}

export function isLibraryClip(generation) {
  if (!generation || generation.error || isDeletedGeneration(generation)) return false;
  if (generation.target !== 'animation') return false;
  if (!LIBRARY_KINDS.includes(generation.kind)) return false;
  return Boolean(generation.output?.path || generation.output?.contentUrl);
}

export function isDeletedGeneration(generation) {
  return Boolean(
    generation?.deleted === true
    || generation?.status === 'deleted'
    || generation?.deletedAt
  );
}

export function isLibraryItem(generation) {
  if (!generation || generation.error || isDeletedGeneration(generation)) return false;
  return Boolean(
    generation.output?.path
    || generation.output?.contentUrl
    || generation.output?.imageUrl
    || generation.storagePath
  );
}

export function libraryItems(generations, { archived = false } = {}) {
  return (generations || [])
    .filter((generation) => {
      if (!isLibraryItem(generation)) return false;
      return archived ? isArchivedClip(generation) : !isArchivedClip(generation);
    })
    .sort((left, right) => String(right.createdAt || '').localeCompare(String(left.createdAt || '')));
}

export function libraryClips(generations, { showArchived = false } = {}) {
  return libraryItems(generations, { archived: showArchived }).filter(isLibraryClip);
}

export function libraryLabel(generation, characters) {
  if (generation?.target === 'treatment-sheet') return 'Treatment';
  if (generation?.target === 'animation') return clipLabel(generation, characters);
  const character = (characters || []).find((item) => item.id === generation?.characterId);
  const title = String(character?.title || character?.name || '').split(':')[0].trim();
  const pose = String(generation?.pose || generation?.target || 'Still').trim();
  return title ? `${title} · ${pose}` : pose;
}

export function clipLabel(generation, characters) {
  const kind = KIND_LABELS[generation?.kind] || 'Clip';
  if (generation?.kind === 'scene' || generation?.kind === 'social-clip') return kind;
  const character = (characters || []).find((item) => item.id === generation?.characterId);
  const title = String(character?.title || character?.name || '').split(':')[0].trim();
  return title ? `${title} · ${kind}` : kind;
}

function isArchivedItem(item) {
  return item?.status === 'archived' || Boolean(item?.archivedAt) || Boolean(item?.archived);
}

export function groupAnimationYears({ chapters, characters, lockedPageIds }) {
  const locked = lockedPageIds || new Set();
  const used = new Set();
  const groups = [];
  for (const chapter of chapters || []) {
    const castIds = new Set((chapter.cast || []).map((member) => member.characterId).filter(Boolean));
    const year = chapter?.year;
    const yearCharacters = (characters || []).filter((character) => {
      if (!character?.id || used.has(character.id) || isArchivedItem(character)) return false;
      if (chapter?.castIsList) return castIds.has(character.id);
      if (castIds.has(character.id)) return true;
      return year != null && year !== '' && character.meta?.year === year;
    });
    yearCharacters.forEach((character) => used.add(character.id));
    const pages = (chapter.pages || []).filter((page) => page?.id && locked.has(page.id) && !isArchivedItem(page));
    if (!yearCharacters.length && !pages.length) continue;
    const calendarYear = /^(?:19|20)\d{2}$/.test(String(year ?? ''));
    const label = chapter?.castIsList
      ? (chapter.title || 'Chapter')
      : (calendarYear ? String(year) : (chapter.title || (year != null && year !== '' ? String(year) : 'Chapter')));
    groups.push({ chapterId: chapter.id, label, characters: yearCharacters, pages });
  }
  return groups;
}

export function clipsForPrompt(generations, { characterId, pageId, kind, pose } = {}) {
  const source = generations || [];
  if (kind === 'scene') {
    const scenes = source.filter((generation) => (
      isLibraryClip(generation)
      && !isArchivedClip(generation)
      && generation.kind === 'scene'
      && (generation.pageIds || []).includes(pageId)
    ));
    const sceneIds = new Set(scenes.map((scene) => scene.id));
    const socials = source.filter((generation) => (
      isLibraryClip(generation)
      && !isArchivedClip(generation)
      && generation.kind === 'social-clip'
      && sceneIds.has(generation.sceneId)
    ));
    return [...scenes, ...socials];
  }
  return source.filter((generation) => (
    isLibraryClip(generation)
    && !isArchivedClip(generation)
    && generation.kind === kind
    && generation.characterId === characterId
    && (!pose || String(generation.pose || '').trim().toLowerCase() === String(pose).trim().toLowerCase())
  ));
}
